// TYPE ROLES — what a piece of text IS, and what that comes to (AG-2125).
//
// A board's type had no hierarchy, only sizes that differed slightly: a tile
// title at 13px, its subtitle at 11, a section label at 11, a KPI label at 12.5,
// a legend at 10, an axis tick at 10. Six things a reader has to rank, set
// within three pixels of each other — so every one of them read as the same
// thing, and a board's actual finding was indistinguishable from its chrome:
//
//     NET REVENUE
//     $70M
//     vs previous period…
//
// where what the board had to say was
//
//     Revenue is slowing
//            ↓
//           $70M
//            ↓
//     −30.3% vs previous period
//
// The fix is not bigger numbers. It is ROLES: a text has a job on the page, the
// design says how sharply those jobs are distinguished, and the renderer decides
// what each comes to — the same division of labour `legibility.ts` already
// applies to every box on the board.
//
// Nothing here is a pixel. Every role is a STEP on a modular scale whose base is
// the application's own root size and whose ratio the design chooses, so the
// whole hierarchy moves with the app, with the density, and with the board's own
// stated character.

import { densityTypeScale, minTileHeightPx, rootFontPx } from './legibility';
import { roleSizeFrom } from './typeRoles';
import {
  ROLE_CASE, ROLE_FIGURES, ROLE_LEADING, ROLE_TRACKING, ROLE_WEIGHT, SURFACE_ROLE, TYPE_ROLES, TYPE_SCALE, TYPE_SURFACES, DEFAULT_SCALE, roleLeading, type TypeRole, type TypeSurface,
} from './typeRoles';
import type { DashTheme } from './types';

export { TYPE_ROLES, TYPE_SURFACES, roleLeading, type TypeRole, type TypeSurface };

/**
 * What a piece of text is FOR. Ordered loudest to quietest, and the order is
 * most of what the roles mean.
 *
 *   display     the board's own name. One per page, or none.
 *   metric      a headline figure. The largest thing in a stat card, and often
 *               the largest thing on the board — a number is what a reader came
 *               for, and it outranks the title above it.
 *   headline    a finding stated in words: "Revenue is slowing".
 *   section     a group label. Quiet by design — it organises, it does not
 *               announce.
 *   annotation  a sentence beside a mark: what a reader should notice.
 *   data        a value drawn among the marks — a bar's label, a tick.
 *   label       what a field or an axis is called.
 *   caption     provenance and qualification: "vs previous period", "excl. tax".
 */
function ratioOf(theme?: DashTheme): number {
  const asked = String((theme as Record<string, unknown> | undefined)?.typeScale || '')
    .trim().toLowerCase();
  return TYPE_SCALE[asked] ?? TYPE_SCALE[DEFAULT_SCALE];
}

/** The size a role comes to on this board, in pixels.
 *
 *  base × ratio^step, where the base is the application's own root size and the
 *  density scales it — so a compact board's whole hierarchy is smaller without
 *  any of the roles losing their order. */
export function roleSizePx(role: TypeRole, theme?: DashTheme): number {
  // 0.8125em is the app's own body-text step; the roles are measured from it, so
  // a board is set in the product's own type rather than in a size chosen here.
  const base = rootFontPx() * 0.8125 * densityTypeScale(theme);
  return Math.round(roleSizeFrom(role, base, ratioOf(theme)) * 100) / 100;
}

/** What a DESIGN may say about one type role (AG-2203).
 *
 *  ⭐ TYPOGRAPHY HAD NO SEMANTIC OWNER AT ALL. `ROLE_WEIGHT`, `ROLE_LEADING`,
 *  `ROLE_CASE`, `ROLE_TRACKING` and `ROLE_FIGURES` were read unconditionally, so
 *  a design could choose the RATIO between sizes and nothing else. A board could
 *  not set its metrics lighter, its section labels in sentence case, or its
 *  headline tighter, however clearly it asked.
 *
 *  ⚠️ THE TABLES HAVE NOT MOVED, AND THAT IS DELIBERATE — relocating them would
 *  be the same authority in a new file. They are now the answer for a role the
 *  design did not speak about, which is a different thing: every one of them is
 *  overridable, per role, by name. What is left in them is typographic fact
 *  (leading tightens as type grows; figures read in a column want tabular
 *  digits), which is the floor a board inherits rather than a taste it is stuck
 *  with.
 */
export type RoleType = {
  /** Any CSS length. A design that states one owns the size outright. */
  size?: string;
  weight?: number;
  /** A multiple of the role's own size, which is what leading is. */
  leading?: number;
  case?: string;
  /** In em. */
  tracking?: number;
  figures?: string;
};

/** Every role as the CSS variables the stylesheet reads.
 *
 *  Published on the board root beside the rest of the theme, so a rule says
 *  `font-size: var(--dt-type-section)` and the hierarchy is one decision in one
 *  place rather than nine literals across a stylesheet. */
export function typographyVars(theme?: DashTheme): Record<string, string> {
  const out: Record<string, string> = {};
  const authored = (theme as { type?: Record<string, RoleType> } | undefined)?.type || {};
  for (const role of TYPE_ROLES) {
    const said = authored[role] || {};
    out[`--dt-type-${role}`] = said.size != null
      ? String(said.size).trim()
      : `${roleSizePx(role, theme)}px`;
    out[`--dt-weight-${role}`] = String(said.weight ?? ROLE_WEIGHT[role]);
    out[`--dt-leading-${role}`] = String(said.leading ?? ROLE_LEADING[role]);
    // ⭐ EVERY ROLE PUBLISHES A TRACK, EVEN WHEN IT IS ZERO (AG-2325).
    //
    // This was `if (tracking !== undefined)`, and `ROLE_TRACKING` is a Partial
    // with three entries — so `--dt-track-label`, `--dt-track-caption` and three
    // others were never published at all. The renderer knew and always wrote
    // `var(--dt-track-<role>, 0)`; the PAGE was told the opposite, that "each
    // has a matching --dt-weight-<role> and --dt-track-<role>" (AG-2285's own
    // text). A page taking the brief at its word wrote
    // `letter-spacing: var(--dt-track-label)` with no fallback, which is an
    // invalid declaration, so the letterspacing vanished from exactly the
    // uppercase label the same brief asks for. Measured live on board 2db492da.
    //
    // Publishing the zero makes the promise true at the SOURCE rather than
    // asking eight consumers to remember a fallback, which is one owner instead
    // of eight. `0` is a real tracking value, not an absence.
    const tracking = said.tracking ?? ROLE_TRACKING[role] ?? 0;
    out[`--dt-track-${role}`] = `${tracking}em`;
    const textCase = said.case ?? ROLE_CASE[role];
    if (textCase) out[`--dt-case-${role}`] = textCase;
    const figures = said.figures ?? ROLE_FIGURES[role];
    if (figures) out[`--dt-figures-${role}`] = figures;
  }
  // WHICH ROLE EACH SURFACE USES (AG-2254).
  //
  // The stylesheet used to name a role directly — `.dt-title` read
  // `--dt-type-headline`, `.dt-tab` read `--dt-type-label` — so the assignment
  // was the renderer's and a board that wanted its tile titles set in `display`
  // had nowhere to say so. It reads `--dt-role-<surface>-*` now, and this points
  // each one at whichever role the design assigned.
  //
  // An INDIRECTION rather than a copy: the surface variable resolves to the
  // role variable, so everything already true of that role — a weight the design
  // overrode, a case it turned off — follows the reassignment without being
  // restated here.
  const assigned = (theme as { roles?: Record<string, string> } | undefined)?.roles || {};
  for (const surface of TYPE_SURFACES) {
    const said = String(assigned[surface] || '').trim().toLowerCase();
    const role = (TYPE_ROLES as readonly string[]).includes(said)
      ? said as TypeRole
      : SURFACE_ROLE[surface];
    out[`--dt-role-${surface}-size`] = `var(--dt-type-${role})`;
    out[`--dt-role-${surface}-weight`] = `var(--dt-weight-${role})`;
    out[`--dt-role-${surface}-leading`] = `var(--dt-leading-${role})`;
    // Tracking, case and figures are only published for the roles that state
    // them, so these carry the same neutral answer the CSS used to inline.
    out[`--dt-role-${surface}-track`] = `var(--dt-track-${role}, 0)`;
    out[`--dt-role-${surface}-case`] = `var(--dt-case-${role}, none)`;
  }
  return out;
}

/** The surfaces a design may reassign, for a caller that wants to validate one. */
export const TYPE_SURFACE_NAMES: readonly string[] = TYPE_SURFACES;

/** The named scales a design may ask for, for a caller that wants to validate
 *  one. `restrained | balanced | dramatic`. */
export const TYPE_SCALES = Object.keys(TYPE_SCALE);


/**
 * The room a tile gets BEFORE anything has been drawn in it (AG-2205).
 *
 * ⭐ THE ONE QUESTION MEASUREMENT CANNOT ANSWER. `chartRuntime.requiredHeightPx`
 * measures a drawing and the tile grows to it, which is what removed the
 * five-"plot-band" guess. But a chart on a CLOSED TAB has not drawn, a chart
 * still loading has not drawn, and a KPI that fits its box perfectly well at 34px
 * never reports anything — it is simply too small to read. Something has to
 * answer for those, and it must not ask what kind of chart it is.
 *
 * ⇒ IT ASKS THE BOARD'S OWN TYPE INSTEAD. The largest text a tile can hold is the
 * `metric` role, and under it sits a label saying what the figure is. A tile with
 * room for those two is a tile any content can begin in and be measured in; a
 * tile with less is one no content can be read in, whatever it holds.
 *
 * So a compact board's floor is lower than a spacious one's and a board set in a
 * dramatic scale gets more than a restrained one — because the board said so,
 * not because this file has an opinion about bar charts.
 *
 * ⚠️ MEASURED, NOT GUESSED, AND STILL NOT A MEASUREMENT OF CONTENT. This is the
 * floor for the undrawn case only. The moment a drawing exists it reports what it
 * needs and this stops being the answer.
 */
export function undrawnFloorPx(theme?: DashTheme, headerless = false): number {
  // One implementation, in `legibility`, because the two were the same answer to
  // the same question and two copies drift. Kept as a name because it says what
  // it is for, where `minTileHeightPx` reads as a general-purpose measurement.
  return minTileHeightPx(undefined, theme, headerless);
}
